import React, { useState, useRef, useEffect } from 'react';
import { isEmpty } from 'ramda';
import { __ } from '@wordpress/i18n';

// components
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import CaretIcon from '../../icons/CaretIcon';

// hooks
import useDebounce from '../../hooks/useDebounce';

function MultiSelectDropdown({
                                 value = [],
                                 onChange,
                                 searchFn,
                                 initialOptions, // Pre-loaded options for immediate display and client-side filtering
                                 debounceMs = 300,
                                 minSearchLength = 2,
                                 labelKey = 'label',
                                 valueKey = 'value',
                                 placeholder = __('Seleziona...', 'soundbetween'),
                                 emptyLabel = __('Nessuna corrispondenza trovata', 'soundbetween'),
                                 displayMode = 'count',
                                 showClearAll = true,
                                 disableSearch = false,
                                 loadingLabel = __('Caricamento...', 'soundbetween'),
                                 optionLabel,
                                 children,
                                 renderSelectedItem
                             }) {
    const refSearch = useRef(null);
    const refSelect = useRef(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedValues, setSelectedValues] = useState(value || []);
    const [selectedObjects, setSelectedObjects] = useState([]);

    const debouncedSearchKeyword = useDebounce(searchKeyword, debounceMs);

    const classesSelect = ['multiSelectDropdown', ...(showDropdown ? ['expanded'] : []), ...(loading ? ['multiSelectDropdown--loading'] : [])];

    const displayDropdown = () => {
        setShowDropdown(true);
    };

    const hideDropdown = () => {
        setShowDropdown(false);
    };

    const hideDropdownOnEsc = (e) => {
        e.stopPropagation();

        if (27 === e.keyCode) {
            setShowDropdown(false);
            if (refSelect && refSelect.current) {
                refSelect.current.focus();
            }
        }
    };

    const onKeyDownWrapper = (e) => {
        if (13 === e.keyCode) {
            if (!showDropdown) {
                setShowDropdown(true);
            }
        }
    };

    const performSearch = async (keyword) => {
        if (!searchFn) {
            return;
        }

        setLoading(true);

        try {
            const results = await searchFn(keyword);
            setOptions(results || []);
        } catch (error) {
            console.error('Search failed:', error);
            setOptions([{
                [valueKey]: 'error',
                [labelKey]: __('Errore', 'soundbetween')
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxToggle = (itemValue, itemObject) => {
        const isSelected = selectedValues.includes(itemValue);
        const newValues = isSelected
            ? selectedValues.filter(v => v !== itemValue)
            : [...selectedValues, itemValue];

        const newObjects = isSelected
            ? selectedObjects.filter(obj => obj[valueKey] !== itemValue)
            : [...selectedObjects, itemObject];

        setSelectedValues(newValues);
        setSelectedObjects(newObjects);
        onChange(newValues);
    };

    const handleClearAll = () => {
        setSelectedValues([]);
        setSelectedObjects([]);
        onChange([]);
    };

    const handleRemoveItem = (itemValue) => {
        const newValues = selectedValues.filter(v => v !== itemValue);
        const newObjects = selectedObjects.filter(obj => obj[valueKey] !== itemValue);

        setSelectedValues(newValues);
        setSelectedObjects(newObjects);
        onChange(newValues);
    };

    const getDisplayText = () => {
        if (!selectedValues.length) {
            return placeholder;
        }

        switch (displayMode) {
            case 'count':
                return `${selectedValues.length} ${
                    selectedValues.length === 1
                        ? __('selezionato', 'soundbetween')
                        : __('selezionati', 'soundbetween')
                }`;

            case 'first':
                const firstItem = options.find(o => o[valueKey] === selectedValues[0]);
                const firstName = firstItem?.[labelKey] || selectedValues[0];
                const remaining = selectedValues.length - 1;
                return remaining > 0
                    ? `${firstName} +${remaining} ${__('più', 'soundbetween')}`
                    : firstName;

            default:
                return `${selectedValues.length} ${
                    selectedValues.length === 1
                        ? __('selezionato', 'soundbetween')
                        : __('selezionati', 'soundbetween')
                }`;
        }
    };

    useEffect(() => {
        setSelectedValues(value || []);
        // If value changes externally, try to preserve objects we have
        if (value) {
            const newObjects = selectedObjects.filter(obj => value.includes(obj[valueKey]));
            if (newObjects.length !== selectedObjects.length) {
                setSelectedObjects(newObjects);
            }
        } else {
            setSelectedObjects([]);
        }
    }, [value]);

    useEffect(() => {
        if (!showDropdown) {
            setSearchKeyword('');
            // Don't clear options if we have initialOptions (they should persist)
            if (!initialOptions) {
                setOptions([]);
            }
        } else {
            // When dropdown opens, load initialOptions if provided
            if (initialOptions) {
                setOptions(initialOptions);
            }
        }
        if (showDropdown && refSearch.current && !disableSearch) {
            refSearch.current.focus();
        }
        const handleClickOutside = (e) => {
            if (refSelect.current && !refSelect.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            window.addEventListener('keydown', hideDropdownOnEsc, false);
            window.addEventListener('click', handleClickOutside);
        }
        return () => {
            window.removeEventListener('keydown', hideDropdownOnEsc, false);
            window.removeEventListener('click', handleClickOutside);
        };
    }, [showDropdown, initialOptions]);

    useEffect(() => {
        // If we have initialOptions, do client-side filtering
        if (initialOptions) {
            if (!debouncedSearchKeyword || debouncedSearchKeyword.trim() === '') {
                // Show all options when search is empty
                setOptions(initialOptions);
                return;
            }

            // Filter options client-side
            const searchTerm = debouncedSearchKeyword.toLowerCase().trim();
            const filtered = initialOptions.filter(option => {
                const label = (option[labelKey] || '').toString().toLowerCase();
                return label.includes(searchTerm);
            });
            setOptions(filtered);
            return;
        }

        // Original behavior for async searchFn
        if (!debouncedSearchKeyword || debouncedSearchKeyword.length < minSearchLength) {
            setOptions([]);
            return;
        }

        performSearch(debouncedSearchKeyword);
    }, [debouncedSearchKeyword, initialOptions, labelKey]);

    return (
        <>
            <div ref={refSelect} className={classesSelect.join(' ')} tabIndex={0} onKeyDown={onKeyDownWrapper}>
                <div className="multiSelectDropdown__valueWrapper" onClick={displayDropdown}>
                    {children}
                    <span className="multiSelectDropdown__value">
                    {getDisplayText()}
                </span>
                </div>
                <div className="multiSelectDropdown__caretIcon" onClick={displayDropdown}>
                    <CaretIcon/>
                </div>
                {showDropdown && (
                    <div className="multiSelectDropdown__dropdown">
                        <div className="multiSelectDropdown__header">
                            {children}
                            <span>{optionLabel ? optionLabel : placeholder}</span>
                            <div className="multiSelectDropdown__closeIcon" onClick={hideDropdown}>
                                <CaretIcon/>
                            </div>
                        </div>

                        {!disableSearch && (
                            <div className="multiSelectDropdown__searchWrapper">
                                <input
                                    ref={refSearch}
                                    value={searchKeyword}
                                    onChange={e => setSearchKeyword(e.target.value)}
                                    className="multiSelectDropdown__search"
                                    placeholder={__('Cerca...', 'soundbetween')}
                                    autoComplete="off"
                                />
                                {loading && <span className="multiSelectDropdown__loadingSpinner">...</span>}
                            </div>
                        )}

                        {showClearAll && selectedValues.length > 0 && (
                            <div className="multiSelectDropdown__actionBar">
                                <button
                                    type="button"
                                    onClick={handleClearAll}
                                    className="multiSelectDropdown__clearAllButton">
                                    {__('Pulisci tutti', 'soundbetween')}
                                </button>
                                <span className="multiSelectDropdown__selectionCount">
                                {selectedValues.length} {selectedValues.length === 1
                                    ? __('selezionato', 'soundbetween')
                                    : __('selezionati', 'soundbetween')}
                            </span>
                            </div>
                        )}

                        <OverlayScrollbarsComponent
                            element="div"
                            options={{ scrollbars: { autoHide: 'never' } }}
                            defer>
                            <div className="multiSelectDropdown__resultsWrapper">
                                {!isEmpty(options) ? (
                                    options.map(option => {
                                        if (option[valueKey] === 'no_matches' || option[valueKey] === 'error') {
                                            return (
                                                <div key={option[valueKey]}>
                                                <span className="multiSelectDropdown__noMatches">
                                                    {option[labelKey] ? option[labelKey] : option[valueKey]}
                                                </span>
                                                </div>
                                            );
                                        }

                                        return (
                                            <label
                                                key={option[valueKey]}
                                                className={`multiSelectDropdown__item${
                                                    selectedValues.includes(option[valueKey])
                                                        ? ' multiSelectDropdown__item--selected'
                                                        : ''
                                                }`}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedValues.includes(option[valueKey])}
                                                    onChange={() => handleCheckboxToggle(option[valueKey], option)}
                                                    className="multiSelectDropdown__itemCheckbox"
                                                />
                                                <span className="multiSelectDropdown__itemLabel">
                                                {option[labelKey] ? option[labelKey] : option[valueKey]}
                                            </span>
                                            </label>
                                        );
                                    })
                                ) : (
                                    <div>
                                    <span className="multiSelectDropdown__noMatches">
                                        {initialOptions
                                            ? emptyLabel
                                            : (searchKeyword.length < minSearchLength
                                                ? __('Scrivi almeno {0} caratteri per fare la ricerca', 'soundbetween').replace('{0}', minSearchLength)
                                                : emptyLabel)}
                                    </span>
                                    </div>
                                )}
                            </div>
                        </OverlayScrollbarsComponent>
                    </div>
                )}
            </div>
            {selectedObjects.length > 0 && (
                <div className="multiSelectDropdown__selectedItems">
                    {selectedObjects.map(item => (
                        <div key={item[valueKey]} className="multiSelectDropdown__selectedItem">
                            {renderSelectedItem ? (
                                renderSelectedItem(item, handleRemoveItem)
                            ) : (
                                <>
                                    <span className="multiSelectDropdown__selectedItemLabel">
                                        {item[labelKey] ? item[labelKey] : item[valueKey]}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveItem(item[valueKey])}
                                        className="multiSelectDropdown__removeBtn"
                                        aria-label={__('Cancella', 'soundbetween')}
                                    />
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}

export default MultiSelectDropdown;
